// src/components/customers/RoomsTable.jsx
import { Link } from "react-router-dom";
import ColoredComments from "../common/ColoredComments";

/**
 * RoomsTable Component
 *
 * Displays measurement areas in a clean table format matching the existing design.
 * Fixed issues: proper React Router Link usage, better responsive design, improved UX.
 *
 * Features:
 * ✅ Proper React Router Link integration
 * ✅ Improved responsive design for mobile
 * ✅ Better table structure and accessibility
 * ✅ Consistent styling with existing UI
 * ✅ Enhanced user experience with proper feedback
 *
 * @param {Object} props - Component props
 * @param {Array} props.rooms - Array of room objects to display
 * @param {Function} props.onEditRoom - Function to handle room editing
 * @param {Function} props.onDeleteRoom - Function to handle room deletion
 * @param {string} props.customerId - Customer ID for generating detail links
 * @param {string} props.jobId - Job ID for generating detail links
 */
const RoomsTable = ({ rooms, onEditRoom, onDeleteRoom, customerId, jobId }) => {
  /**
   * Handle room deletion with confirmation
   * Shows a more detailed confirmation dialog
   */
  const handleDeleteRoom = (room) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${room.name}"?\n\nThis will permanently remove:\n• All room measurements\n• Any uploaded images\n• Associated comments\n\nThis action cannot be undone.`,
      )
    ) {
      onDeleteRoom(room);
    }
  };

  // Note truncation now lives in <ColoredComments maxLength={100} />, which
  // truncates by plain-character count first and then clips the colour
  // segments to the same length — so the 100-char cut and the trailing "..."
  // are byte-for-byte what they were before colouring existed.
  const COMMENT_PREVIEW_LENGTH = 100;

  // Show empty state if no rooms exist
  if (!rooms || rooms.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Empty State Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Measurements</h3>
        </div>

        {/* Empty State Content */}
        <div className="p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>

          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Measurements Added
          </h3>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            Get started by adding your first measurement area for this job. You
            can add rooms, hallways, or any area that needs flooring work.
          </p>

          <button
            onClick={() => onEditRoom()}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add First Measurement
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Table Header Section */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Measurements</h3>
          <span className="text-sm text-gray-500">
            {rooms.length} measurement{rooms.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Responsive Table Container */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {/* Table Header */}
          <thead className="bg-gray-50">
            <tr>
              {/* Name Column Header */}
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Name
              </th>

              {/* Comment Column Header */}
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Comment
              </th>

              {/* Actions Column Header */}
              <th
                scope="col"
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="bg-white divide-y divide-gray-200">
            {rooms.map((room, index) => (
              <tr
                key={room.id || index}
                className={`${
                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                } hover:bg-indigo-50 transition-colors duration-150`}
              >
                {/* Room Name Cell */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {/* Measurement Icon */}
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                      <svg
                        className="w-4 h-4 text-indigo-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                    </div>

                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {room.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Measurement #{index + 1}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Room Comment Cell */}
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs">
                    {room.comments ? (
                      <div>
                        <p className="leading-5" title={room.comments}>
                          <ColoredComments
                            text={room.comments}
                            segments={room.commentsSegments}
                            maxLength={COMMENT_PREVIEW_LENGTH}
                          />
                        </p>
                        {room.comments.length > COMMENT_PREVIEW_LENGTH && (
                          <span className="text-xs text-indigo-600 font-medium cursor-help">
                            ...read more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic text-sm">
                        No comments added
                      </span>
                    )}
                  </div>
                </td>

                {/* Actions Cell */}
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center space-x-2">
                    {/* Edit Button */}
                    <button
                      onClick={() => onEditRoom(room)}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 border border-indigo-200 rounded-md hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
                      title="Edit measurement details"
                    >
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteRoom(room)}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-red-600 bg-red-100 border border-red-200 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200"
                      title="Delete this measurement permanently"
                    >
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>

                    {/* Details Link - Fixed with proper React Router Link 
                    <Link
                      to={`/pandm/customers/${customerId}/jobs/${jobId}/rooms/${room.id}`}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-600 bg-green-100 border border-green-200 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 no-underline"
                      title="View measurement details and manage images"
                    >
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      Details
                    </Link>
                    */}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table Footer with Summary */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <span>
              <span className="font-medium text-gray-900">{rooms.length}</span>{" "}
              total measurement{rooms.length !== 1 ? "s" : ""}
            </span>

            <span>
              <span className="font-medium text-green-600">
                {
                  rooms.filter((room) => room.comments && room.comments.trim())
                    .length
                }
              </span>{" "}
              with comments
            </span>
          </div>
          {/*}
          <div className="text-xs text-gray-500">
            Click "Details" to manage measurement images
          </div>
          */}
        </div>
      </div>
    </div>
  );
};

export default RoomsTable;
